import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import {
  BarChart3, PieChart as PieIcon, TrendingUp, Hash, Plus, Trash2, Edit, Share2,
  Loader2, LayoutDashboard, Copy, Check, Settings, ChevronDown, GripVertical, FileText, Info, Filter, Calendar,
  ArrowUpRight, ArrowDownRight, DollarSign, Percent, ListOrdered
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#06b6d4", "#84cc16"];

const CHART_TYPE_LABELS = {
  bar: "Barras",
  line: "Lineas",
  pie: "Circular",
  donut: "Donut",
  kpi: "KPI / Indicador",
  info: "Tarjeta Info",
};

const SIZE_LABELS = { quarter: "1/4", half: "1/2", full: "Completo" };

const OP_LABELS = { count: "Contar", sum: "Sumar", avg: "Promedio", min: "Minimo", max: "Maximo" };

const FORMAT_LABELS = {
  numero: "Numero",
  moneda: "Moneda (RD$ / US$)",
  porcentaje: "Porcentaje",
};

function formatNumber(num) {
  if (num == null || isNaN(num)) return "0";
  return num.toLocaleString("es-DO", { maximumFractionDigits: 2 });
}

function formatKpiValue(num, formato, label) {
  if (num == null || isNaN(num)) return "0";
  const formatted = num.toLocaleString("es-DO", { maximumFractionDigits: 2 });
  if (formato === "moneda") {
    if (label && /US\$/i.test(label)) return `US$ ${formatted}`;
    if (label && /RD\$/i.test(label)) return `RD$ ${formatted}`;
    return formatted;
  }
  if (formato === "porcentaje") return `${formatted}%`;
  return formatted;
}

function formatAxis(num) {
  if (num == null || isNaN(num)) return "0";
  if (Math.abs(num) >= 1e9) return (num / 1e9).toFixed(1) + "B";
  if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(1) + "M";
  if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(0) + "K";
  return num.toLocaleString("es-DO", { maximumFractionDigits: 0 });
}

const REFRESH_OPTIONS = [
  { value: 0, label: "Sin auto-refresh" },
  { value: 30, label: "Cada 30 seg" },
  { value: 60, label: "Cada 1 min" },
  { value: 300, label: "Cada 5 min" },
  { value: 600, label: "Cada 10 min" },
  { value: 1800, label: "Cada 30 min" },
];

const LIMIT_OPTIONS = [5, 10, 15, 20, 30, 50];

const MESES = [
  { value: "01", label: "Enero" }, { value: "02", label: "Febrero" }, { value: "03", label: "Marzo" },
  { value: "04", label: "Abril" }, { value: "05", label: "Mayo" }, { value: "06", label: "Junio" },
  { value: "07", label: "Julio" }, { value: "08", label: "Agosto" }, { value: "09", label: "Septiembre" },
  { value: "10", label: "Octubre" }, { value: "11", label: "Noviembre" }, { value: "12", label: "Diciembre" },
];
const TRIMESTRES = [
  { value: "1", label: "Q1 (Ene-Mar)" }, { value: "2", label: "Q2 (Abr-Jun)" },
  { value: "3", label: "Q3 (Jul-Sep)" }, { value: "4", label: "Q4 (Oct-Dic)" },
];

export default function DashboardBIPage() {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [activeDash, setActiveDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDash, setShowCreateDash] = useState(false);
  const [editingDash, setEditingDash] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showChartBuilder, setShowChartBuilder] = useState(false);
  const [editingChart, setEditingChart] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchDashboards = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/dashboards-bi/dashboards`, getAuthHeaders());
      setDashboards(res.data || []);
      if (res.data.length > 0 && !activeDash) {
        const target = dashboardId ? res.data.find(d => d.id === dashboardId) : res.data[0];
        setActiveDash(target || res.data[0]);
      }
    } catch {
      toast.error("Error cargando dashboards");
    } finally {
      setLoading(false);
    }
  }, [dashboardId]);

  useEffect(() => { fetchDashboards(); }, [fetchDashboards]);

  const handleDeleteDash = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/api/dashboards-bi/dashboards/${confirmDelete}`, getAuthHeaders());
      toast.success("Dashboard eliminado");
      setConfirmDelete(null);
      if (activeDash?.id === confirmDelete) setActiveDash(null);
      fetchDashboards();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error eliminando");
    }
  };

  const handleSaveChart = async (chartConfig) => {
    if (!activeDash) return;
    const charts = [...(activeDash.charts || [])];
    const idx = charts.findIndex(c => c.id === chartConfig.id);
    if (idx >= 0) {
      charts[idx] = chartConfig;
    } else {
      chartConfig.id = crypto.randomUUID();
      chartConfig.order = charts.length;
      charts.push(chartConfig);
    }

    try {
      await axios.put(`${API}/api/dashboards-bi/dashboards/${activeDash.id}`, { charts }, getAuthHeaders());
      setActiveDash({ ...activeDash, charts });
      setShowChartBuilder(false);
      setEditingChart(null);
      toast.success("Grafica guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    }
  };

  const handleDeleteChart = async (chartId) => {
    if (!activeDash) return;
    const charts = activeDash.charts.filter(c => c.id !== chartId);
    try {
      await axios.put(`${API}/api/dashboards-bi/dashboards/${activeDash.id}`, { charts }, getAuthHeaders());
      setActiveDash({ ...activeDash, charts });
      toast.success("Grafica eliminada");
    } catch {
      toast.error("Error eliminando grafica");
    }
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 overflow-auto p-6 ml-0 md:ml-64">
        <div className="max-w-[95%] mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="bi-title">
                <BarChart3 className="w-6 h-6 text-indigo-500" />
                Estadisticas Empresariales
              </h1>
              <p className="text-sm text-slate-500 mt-1">Dashboards interactivos con datos de los flujos de automatizacion</p>
            </div>
            <div className="flex gap-2">
              {activeDash && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setShowShareModal(true)} data-testid="share-dashboard-btn">
                    <Share2 className="w-4 h-4 mr-1" /> Compartir
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setEditingChart(null); setShowChartBuilder(true); }} data-testid="add-chart-btn">
                    <Plus className="w-4 h-4 mr-1" /> Nueva Grafica
                  </Button>
                </>
              )}
              <Button size="sm" onClick={() => { setEditingDash(null); setShowCreateDash(true); }} data-testid="add-dashboard-btn">
                <Plus className="w-4 h-4 mr-1" /> Nuevo Dashboard
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
          ) : dashboards.length === 0 ? (
            <Card><CardContent className="py-16 text-center"><LayoutDashboard className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">No hay dashboards creados</p><p className="text-xs text-slate-400 mt-1">Cree un dashboard y agregue graficas conectadas a sus colecciones de datos</p></CardContent></Card>
          ) : (
            <>
              <div className="flex items-center gap-1 border-b overflow-x-auto pb-0">
                {dashboards.map(d => (
                  <button
                    key={d.id}
                    onClick={() => { setActiveDash(d); navigate(`/estadisticas-empresariales/${d.id}`, { replace: true }); }}
                    className={`group flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 transition-colors whitespace-nowrap ${
                      activeDash?.id === d.id
                        ? "border-indigo-500 text-indigo-600 font-medium bg-indigo-50/50"
                        : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`dash-tab-${d.id}`}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    {d.nombre}
                    <Badge variant="secondary" className="text-[10px] ml-1 px-1.5">{(activeDash?.id === d.id ? (activeDash.charts || []).length : (d.charts || []).length)}</Badge>
                    <span className="hidden group-hover:flex items-center gap-0.5 ml-1">
                      <span role="button" tabIndex={0} onClick={e => { e.stopPropagation(); setEditingDash(d); setShowCreateDash(true); }} className="p-0.5 rounded hover:bg-slate-200 cursor-pointer"><Edit className="w-3 h-3 text-slate-400" /></span>
                      <span role="button" tabIndex={0} onClick={e => { e.stopPropagation(); setConfirmDelete(d.id); }} className="p-0.5 rounded hover:bg-red-100 cursor-pointer"><Trash2 className="w-3 h-3 text-red-400" /></span>
                    </span>
                  </button>
                ))}
              </div>

              {activeDash && (
                <DashboardGrid
                  dashboard={activeDash}
                  getAuthHeaders={getAuthHeaders}
                  onEditChart={(c) => { setEditingChart(c); setShowChartBuilder(true); }}
                  onDeleteChart={handleDeleteChart}
                />
              )}
            </>
          )}
        </div>

        <DashboardFormDialog
          open={showCreateDash}
          onOpenChange={setShowCreateDash}
          dashboard={editingDash}
          getAuthHeaders={getAuthHeaders}
          onSaved={(d) => { fetchDashboards(); setShowCreateDash(false); if (d) setActiveDash(d); }}
        />

        <ChartBuilderDialog
          open={showChartBuilder}
          onOpenChange={setShowChartBuilder}
          chart={editingChart}
          getAuthHeaders={getAuthHeaders}
          onSave={handleSaveChart}
        />

        {activeDash && (
          <ShareDashboardModal
            open={showShareModal}
            onOpenChange={setShowShareModal}
            dashboard={activeDash}
            getAuthHeaders={getAuthHeaders}
          />
        )}

        <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar Dashboard</AlertDialogTitle>
              <AlertDialogDescription>Se eliminara el dashboard y todas sus graficas. Esta accion no se puede deshacer.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteDash} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}


// ============== DASHBOARD GRID ==============
function DashboardGrid({ dashboard, getAuthHeaders, onEditChart, onDeleteChart }) {
  const charts = (dashboard.charts || []).sort((a, b) => a.order - b.order);
  const refreshInterval = dashboard.refresh_interval || 0;

  const [collections, setCollections] = useState([]);
  const [filtroColeccion, setFiltroColeccion] = useState("");
  const [filtroColumnaFecha, setFiltroColumnaFecha] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [filtroAnio, setFiltroAnio] = useState("");
  const [filtroPeriodoTipo, setFiltroPeriodoTipo] = useState("anual");
  const [filtroMes, setFiltroMes] = useState("");
  const [filtroTrimestre, setFiltroTrimestre] = useState("");
  const [loadingYears, setLoadingYears] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/dashboards-bi/collections-meta`, getAuthHeaders())
      .then(res => setCollections(res.data || []))
      .catch(() => {});
  }, []);

  const selectedColMeta = collections.find(c => c.id === filtroColeccion);
  const dateColumns = (selectedColMeta?.columnas || []).filter(c => /fecha|date|inicio|final|vence|emision/i.test(c));

  useEffect(() => {
    if (!filtroColeccion || !filtroColumnaFecha) { setAvailableYears([]); return; }
    setLoadingYears(true);
    axios.get(`${API}/api/dashboards-bi/date-periods/${filtroColeccion}/${filtroColumnaFecha}`, getAuthHeaders())
      .then(res => setAvailableYears(res.data?.years || []))
      .catch(() => setAvailableYears([]))
      .finally(() => setLoadingYears(false));
  }, [filtroColeccion, filtroColumnaFecha]);

  const dateFilters = filtroAnio ? {
    filtro_columna_fecha: filtroColumnaFecha,
    filtro_anio: filtroAnio,
    filtro_mes: filtroPeriodoTipo === "mensual" ? filtroMes : "",
    filtro_trimestre: filtroPeriodoTipo === "trimestral" ? filtroTrimestre : "",
  } : {};

  const clearFilters = () => {
    setFiltroColeccion(""); setFiltroColumnaFecha(""); setFiltroAnio("");
    setFiltroPeriodoTipo("anual"); setFiltroMes(""); setFiltroTrimestre("");
  };

  const hasActiveFilter = !!filtroAnio;

  return (
    <div className="space-y-4">
      <Card className="border-dashed">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600">
              <Filter className="w-4 h-4" /> Filtrar por fecha
            </div>
            <select value={filtroColeccion} onChange={e => { setFiltroColeccion(e.target.value); setFiltroColumnaFecha(""); setFiltroAnio(""); }}
              className="h-8 px-2 rounded border text-xs bg-white min-w-[140px]" data-testid="filter-collection">
              <option value="">Coleccion...</option>
              {collections.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
            {filtroColeccion && (
              <select value={filtroColumnaFecha} onChange={e => { setFiltroColumnaFecha(e.target.value); setFiltroAnio(""); }}
                className="h-8 px-2 rounded border text-xs bg-white min-w-[120px]" data-testid="filter-date-col">
                <option value="">Columna fecha...</option>
                {(selectedColMeta?.columnas || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {filtroColumnaFecha && (
              <select value={filtroAnio} onChange={e => setFiltroAnio(e.target.value)}
                className="h-8 px-2 rounded border text-xs bg-white min-w-[90px]" data-testid="filter-year">
                <option value="">Anio...</option>
                {loadingYears ? <option disabled>Cargando...</option> : availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {filtroAnio && (
              <>
                <select value={filtroPeriodoTipo} onChange={e => { setFiltroPeriodoTipo(e.target.value); setFiltroMes(""); setFiltroTrimestre(""); }}
                  className="h-8 px-2 rounded border text-xs bg-white min-w-[110px]" data-testid="filter-period-type">
                  <option value="anual">Anual</option>
                  <option value="trimestral">Trimestral</option>
                  <option value="mensual">Mensual</option>
                </select>
                {filtroPeriodoTipo === "trimestral" && (
                  <select value={filtroTrimestre} onChange={e => setFiltroTrimestre(e.target.value)}
                    className="h-8 px-2 rounded border text-xs bg-white min-w-[120px]" data-testid="filter-quarter">
                    <option value="">Trimestre...</option>
                    {TRIMESTRES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                )}
                {filtroPeriodoTipo === "mensual" && (
                  <select value={filtroMes} onChange={e => setFiltroMes(e.target.value)}
                    className="h-8 px-2 rounded border text-xs bg-white min-w-[110px]" data-testid="filter-month">
                    <option value="">Mes...</option>
                    {MESES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                )}
              </>
            )}
            {hasActiveFilter && (
              <Button variant="ghost" size="sm" className="h-8 text-xs text-red-500 hover:text-red-700" onClick={clearFilters} data-testid="clear-filters-btn">
                Limpiar filtros
              </Button>
            )}
            {hasActiveFilter && (
              <Badge variant="secondary" className="text-[10px] bg-indigo-50 text-indigo-600">
                <Calendar className="w-3 h-3 mr-1" />
                {filtroAnio}{filtroMes ? ` / ${MESES.find(m => m.value === filtroMes)?.label}` : filtroTrimestre ? ` / ${TRIMESTRES.find(t => t.value === filtroTrimestre)?.label}` : ""}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-4 gap-4" data-testid="charts-grid">
        {charts.map(chart => (
          <div key={chart.id} className={`${chart.size === "full" ? "col-span-4" : chart.size === "half" ? "col-span-2" : "col-span-1"}`}>
            <ChartCard
              chart={chart}
              getAuthHeaders={getAuthHeaders}
              onEdit={() => onEditChart(chart)}
              onDelete={() => onDeleteChart(chart.id)}
              refreshInterval={refreshInterval}
              dateFilters={dateFilters}
            />
          </div>
        ))}
        {charts.length === 0 && (
          <div className="col-span-4 text-center py-16">
            <BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No hay graficas en este dashboard</p>
            <p className="text-xs text-slate-400 mt-1">Use el boton "Nueva Grafica" para agregar indicadores y charts</p>
          </div>
        )}
      </div>
    </div>
  );
}


// ============== CHART CARD ==============
function ChartCard({ chart, getAuthHeaders, onEdit, onDelete, isPublic, publicToken, refreshInterval, dateFilters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const payload = {
        coleccion_id: chart.coleccion_id,
        columna_x: chart.columna_x,
        columna_y: chart.columna_y,
        operacion: chart.operacion,
        tipo: chart.tipo,
        limit: chart.limit || 20,
        segmentar_por: chart.segmentar_por || "",
        ...(dateFilters || {}),
      };
      let res;
      if (isPublic && publicToken) {
        res = await axios.post(`${API}/api/dashboards-bi/public/${publicToken}/aggregate`, payload);
      } else {
        res = await axios.post(`${API}/api/dashboards-bi/aggregate`, payload, getAuthHeaders());
      }
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [chart, isPublic, publicToken, dateFilters]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);
  useEffect(() => {
    if (!refreshInterval || refreshInterval <= 0) return;
    const timer = setInterval(fetchData, refreshInterval * 1000);
    return () => clearInterval(timer);
  }, [refreshInterval, fetchData]);

  const editDeleteBtns = !isPublic && (
    <div className="absolute top-2 right-2 hidden group-hover:flex gap-1 z-10">
      <button onClick={onEdit} className="p-1 rounded bg-white/80 hover:bg-white shadow-sm" data-testid={`edit-chart-${chart.id}`}><Edit className="w-3.5 h-3.5 text-slate-500" /></button>
      <button onClick={onDelete} className="p-1 rounded bg-white/80 hover:bg-red-50 shadow-sm" data-testid={`delete-chart-${chart.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
    </div>
  );

  if (loading) {
    return <Card className="h-full"><CardContent className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></CardContent></Card>;
  }

  // INFO card
  if (chart.tipo === "info") {
    return (
      <Card className="h-full group relative" data-testid={`chart-${chart.id}`}>
        {editDeleteBtns}
        <CardContent className="p-5">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{chart.titulo}</p>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{chart.contenido_texto || ""}</p>
        </CardContent>
      </Card>
    );
  }

  // KPI Card - Enhanced
  if (chart.tipo === "kpi") {
    const segments = data?.segments;
    const formato = chart.formato || "numero";
    const subtitulo = chart.subtitulo || "";
    const opLabel = OP_LABELS[chart.operacion] || chart.operacion;
    const colY = chart.columna_y;

    return (
      <Card className="h-full group relative overflow-hidden" data-testid={`chart-${chart.id}`}>
        {editDeleteBtns}
        <div className="absolute top-0 left-0 w-full h-1" style={{ backgroundColor: chart.color || "#6366f1" }} />
        <CardContent className="p-5 pt-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{chart.titulo}</p>
              {subtitulo && <p className="text-[10px] text-slate-400 mt-0.5">{subtitulo}</p>}
            </div>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: (chart.color || "#6366f1") + "15" }}>
              {chart.operacion === "count" ? <ListOrdered className="w-4 h-4" style={{ color: chart.color || "#6366f1" }} /> :
               chart.operacion === "sum" ? <DollarSign className="w-4 h-4" style={{ color: chart.color || "#6366f1" }} /> :
               <Hash className="w-4 h-4" style={{ color: chart.color || "#6366f1" }} />}
            </div>
          </div>

          {segments && segments.length > 0 ? (
            <div className="mt-3 space-y-2">
              {segments.map((seg, i) => (
                <div key={i} className="flex items-baseline justify-between border-b border-slate-100 pb-1.5 last:border-0">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: chart.color || "#6366f1", opacity: 1 - (i * 0.3) }} />
                    <span className="text-[11px] font-medium text-slate-500">{seg.label}</span>
                    {chart.mostrar_conteo !== false && (
                      <span className="text-[9px] text-slate-300">({formatNumber(seg.count)} reg.)</span>
                    )}
                  </div>
                  <span className="text-lg font-bold tabular-nums" style={{ color: chart.color || "#6366f1" }}>
                    {formatKpiValue(seg.value, formato, seg.label)}
                  </span>
                </div>
              ))}
              <div className="flex items-baseline justify-between pt-1 border-t border-slate-200">
                <span className="text-[10px] font-semibold text-slate-400 uppercase">Total</span>
                <span className="text-sm font-bold text-slate-700 tabular-nums">
                  {formatKpiValue(data?.value || 0, formato, "")}
                </span>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              <p className="text-3xl font-bold tabular-nums" style={{ color: chart.color || "#6366f1" }}>
                {formatKpiValue(data?.value || 0, formato, "")}
              </p>
              {colY && (
                <p className="text-[10px] text-slate-400 mt-1">{opLabel} de {colY}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Chart Cards (bar, line, pie, donut)
  const chartData = (data?.labels || []).map((label, i) => ({
    name: label.length > 25 ? label.slice(0, 25) + "..." : label,
    fullName: label,
    value: data.values[i] || 0,
  }));
  const colors = chart.colores || CHART_COLORS;

  const customTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-2">
        <p className="text-xs font-semibold text-slate-800">{d.fullName || d.name}</p>
        <p className="text-sm font-bold" style={{ color: chart.color || "#6366f1" }}>{formatNumber(d.value)}</p>
      </div>
    );
  };

  const CompactLegend = ({ items, colorFn }) => (
    <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center mt-1 px-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colorFn(i) }} />
          <span className="text-[10px] text-slate-500 truncate max-w-[100px]" title={item.fullName}>{item.name}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Card className="h-full group relative" data-testid={`chart-${chart.id}`}>
      {editDeleteBtns}
      <CardContent className="p-4 flex flex-col items-center">
        {chartData.length === 0 ? (
          <>
            <p className="text-xs text-slate-400 text-center py-8">Sin datos</p>
            <p className="text-xs font-medium text-slate-500 mt-2 tracking-wide">{chart.titulo}</p>
          </>
        ) : chart.tipo === "bar" ? (
          <>
            <ResponsiveContainer width="100%" height={chart.size === "full" ? 300 : 220}>
              <BarChart data={chartData} margin={{ left: 5, right: 5, bottom: 5, top: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={false} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} height={5} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={formatAxis} axisLine={false} tickLine={false} />
                <Tooltip content={customTooltip} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs font-medium text-slate-600 mt-1 tracking-wide">{chart.titulo}</p>
            <CompactLegend items={chartData} colorFn={(i) => colors[i % colors.length]} />
          </>
        ) : chart.tipo === "line" ? (
          <>
            <ResponsiveContainer width="100%" height={chart.size === "full" ? 300 : 220}>
              <LineChart data={chartData} margin={{ left: 5, right: 5, bottom: 5, top: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={false} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} height={5} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={formatAxis} axisLine={false} tickLine={false} />
                <Tooltip content={customTooltip} />
                <Line type="monotone" dataKey="value" stroke={chart.color || "#6366f1"} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs font-medium text-slate-600 mt-1 tracking-wide">{chart.titulo}</p>
            <CompactLegend items={chartData} colorFn={() => chart.color || "#6366f1"} />
          </>
        ) : (chart.tipo === "pie" || chart.tipo === "donut") ? (
          <>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={chartData} cx="50%" cy="50%" innerRadius={chart.tipo === "donut" ? 40 : 0} outerRadius={80} paddingAngle={2} dataKey="value" nameKey="name">
                  {chartData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                </Pie>
                <Tooltip formatter={(val, name) => [formatNumber(val), name]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
              </PieChart>
            </ResponsiveContainer>
            <p className="text-xs font-medium text-slate-600 mt-1 tracking-wide">{chart.titulo}</p>
            <CompactLegend items={chartData} colorFn={(i) => colors[i % colors.length]} />
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}


// ============== DASHBOARD FORM ==============
function DashboardFormDialog({ open, onOpenChange, dashboard, getAuthHeaders, onSaved }) {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [acceso, setAcceso] = useState("admin");
  const [refreshInterval, setRefreshInterval] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (dashboard) {
        setNombre(dashboard.nombre || "");
        setDescripcion(dashboard.descripcion || "");
        setAcceso(dashboard.acceso || "admin");
        setRefreshInterval(dashboard.refresh_interval || 0);
      } else {
        setNombre(""); setDescripcion(""); setAcceso("admin"); setRefreshInterval(0);
      }
    }
  }, [open, dashboard]);

  const handleSave = async () => {
    if (!nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (dashboard) {
        await axios.put(`${API}/api/dashboards-bi/dashboards/${dashboard.id}`, { nombre, descripcion, acceso, refresh_interval: refreshInterval }, getAuthHeaders());
        toast.success("Dashboard actualizado");
        onSaved(null);
      } else {
        const res = await axios.post(`${API}/api/dashboards-bi/dashboards`, { nombre, descripcion, acceso, charts: [], refresh_interval: refreshInterval }, getAuthHeaders());
        toast.success("Dashboard creado");
        onSaved(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dashboard-form-dialog">
        <DialogHeader>
          <DialogTitle>{dashboard ? "Editar Dashboard" : "Nuevo Dashboard"}</DialogTitle>
          <DialogDescription>Configure el nombre y nivel de acceso del dashboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Nombre *</Label><Input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Resumen de Facturacion" data-testid="dash-name-input" /></div>
          <div><Label>Descripcion</Label><Input value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Descripcion opcional" /></div>
          <div>
            <Label>Acceso</Label>
            <select value={acceso} onChange={e => setAcceso(e.target.value)} className="w-full h-9 px-3 rounded-md border text-sm bg-white">
              <option value="admin">Solo Administradores</option>
              <option value="todos">Todos los Usuarios</option>
            </select>
          </div>
          <div>
            <Label>Auto-actualizacion de graficas</Label>
            <select value={refreshInterval} onChange={e => setRefreshInterval(Number(e.target.value))} className="w-full h-9 px-3 rounded-md border text-sm bg-white" data-testid="refresh-interval-select">
              {REFRESH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-dash-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {dashboard ? "Guardar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============== CHART BUILDER ==============
function ChartBuilderDialog({ open, onOpenChange, chart, getAuthHeaders, onSave }) {
  const [collections, setCollections] = useState([]);
  const [tipo, setTipo] = useState("bar");
  const [titulo, setTitulo] = useState("");
  const [coleccionId, setColeccionId] = useState("");
  const [columnaX, setColumnaX] = useState("");
  const [columnaY, setColumnaY] = useState("");
  const [operacion, setOperacion] = useState("count");
  const [color, setColor] = useState("#6366f1");
  const [size, setSize] = useState("half");
  const [limit, setLimit] = useState(10);
  const [segmentarPor, setSegmentarPor] = useState("");
  const [contenidoTexto, setContenidoTexto] = useState("");
  const [subtitulo, setSubtitulo] = useState("");
  const [formato, setFormato] = useState("numero");
  const [mostrarConteo, setMostrarConteo] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/dashboards-bi/collections-meta`, getAuthHeaders())
        .then(res => setCollections(res.data || []))
        .catch(() => {});

      if (chart) {
        setTipo(chart.tipo || "bar");
        setTitulo(chart.titulo || "");
        setColeccionId(chart.coleccion_id || "");
        setColumnaX(chart.columna_x || "");
        setColumnaY(chart.columna_y || "");
        setOperacion(chart.operacion || "count");
        setColor(chart.color || "#6366f1");
        setSize(chart.size || "half");
        setLimit(chart.limit || 10);
        setSegmentarPor(chart.segmentar_por || "");
        setContenidoTexto(chart.contenido_texto || "");
        setSubtitulo(chart.subtitulo || "");
        setFormato(chart.formato || "numero");
        setMostrarConteo(chart.mostrar_conteo !== false);
      } else {
        setTipo("bar"); setTitulo(""); setColeccionId(""); setColumnaX("");
        setColumnaY(""); setOperacion("count"); setColor("#6366f1"); setSize("half");
        setLimit(10); setSegmentarPor(""); setContenidoTexto(""); setPreviewData(null);
        setSubtitulo(""); setFormato("numero"); setMostrarConteo(true);
      }
    }
  }, [open, chart]);

  const selectedCollection = collections.find(c => c.id === coleccionId);
  const columns = selectedCollection?.columnas || [];

  const handlePreview = async () => {
    if (tipo === "info") return;
    if (!coleccionId) return;
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${API}/api/dashboards-bi/aggregate`, {
        coleccion_id: coleccionId, columna_x: columnaX, columna_y: columnaY,
        operacion, tipo, limit, segmentar_por: segmentarPor,
      }, getAuthHeaders());
      setPreviewData(res.data);
    } catch {
      toast.error("Error en vista previa");
    } finally {
      setLoadingPreview(false);
    }
  };

  // Auto-preview for KPI when collection or columns change
  useEffect(() => {
    if (tipo === "kpi" && coleccionId) {
      const timer = setTimeout(() => {
        handlePreview();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [tipo, coleccionId, columnaY, operacion, segmentarPor]);

  const handleSave = () => {
    if (!titulo.trim()) { toast.error("El titulo es obligatorio"); return; }
    if (tipo !== "info" && !coleccionId) { toast.error("Seleccione una coleccion"); return; }
    if (!["kpi", "info"].includes(tipo) && !columnaX) { toast.error("Seleccione el campo de agrupacion"); return; }
    onSave({
      id: chart?.id || "",
      tipo, titulo: titulo.trim(), coleccion_id: coleccionId,
      columna_x: columnaX, columna_y: columnaY, operacion,
      color, colores: null, order: chart?.order || 0, size, limit,
      segmentar_por: segmentarPor, contenido_texto: contenidoTexto,
      subtitulo: subtitulo.trim(), formato, mostrar_conteo: mostrarConteo,
    });
  };

  const isKpi = tipo === "kpi";
  const isInfo = tipo === "info";
  const isChart = !isKpi && !isInfo;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="chart-builder-dialog">
        <DialogHeader>
          <DialogTitle>{chart ? "Editar Grafica" : "Nueva Grafica"}</DialogTitle>
          <DialogDescription>
            {isKpi ? "Configure un indicador numerico (KPI) con los datos de sus colecciones." :
             isInfo ? "Cree una tarjeta informativa con texto personalizado." :
             "Configure la fuente de datos y tipo de visualizacion."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-5">
          {/* Left: Config */}
          <div className="space-y-3">
            {/* Type Selector */}
            <div>
              <Label className="text-xs text-slate-500">Tipo de Visualizacion</Label>
              <div className="grid grid-cols-6 gap-1.5 mt-1">
                {Object.entries(CHART_TYPE_LABELS).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => { setTipo(k); setPreviewData(null); }}
                    className={`p-2 rounded-lg border text-[10px] flex flex-col items-center gap-1 transition-colors ${tipo === k ? "border-indigo-500 bg-indigo-50 text-indigo-700 font-medium" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
                    data-testid={`chart-type-${k}`}>
                    {k === "bar" ? <BarChart3 className="w-4 h-4" /> : k === "line" ? <TrendingUp className="w-4 h-4" /> : k === "pie" || k === "donut" ? <PieIcon className="w-4 h-4" /> : k === "info" ? <FileText className="w-4 h-4" /> : <Hash className="w-4 h-4" />}
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {/* ===== KPI FORM ===== */}
            {isKpi && (
              <>
                <div className="bg-indigo-50/50 border border-indigo-100 rounded-lg p-3 space-y-3">
                  <p className="text-[11px] font-semibold text-indigo-600 uppercase tracking-wide flex items-center gap-1">
                    <Hash className="w-3.5 h-3.5" /> Configuracion del KPI
                  </p>

                  <div>
                    <Label className="text-xs">Titulo de la tarjeta *</Label>
                    <Input value={titulo} onChange={e => setTitulo(e.target.value)}
                      placeholder="Ej: Prima Total, Valor Asegurado, Total Polizas"
                      className="h-8 text-sm" data-testid="chart-title-input" />
                  </div>

                  <div>
                    <Label className="text-xs">Subtitulo / Descripcion</Label>
                    <Input value={subtitulo} onChange={e => setSubtitulo(e.target.value)}
                      placeholder="Ej: Suma de primas del periodo actual"
                      className="h-8 text-sm" data-testid="chart-subtitle-input" />
                  </div>

                  <div>
                    <Label className="text-xs">Coleccion de Datos *</Label>
                    <select value={coleccionId} onChange={e => { setColeccionId(e.target.value); setColumnaY(""); setSegmentarPor(""); }}
                      className="w-full h-8 px-3 rounded-md border text-sm bg-white" data-testid="chart-collection-select">
                      <option value="">Seleccione una coleccion...</option>
                      {collections.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.total_registros} reg.)</option>)}
                    </select>
                  </div>

                  {coleccionId && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Que desea calcular? *</Label>
                          <select value={operacion} onChange={e => setOperacion(e.target.value)}
                            className="w-full h-8 px-3 rounded-md border text-sm bg-white" data-testid="chart-operation-select">
                            {Object.entries(OP_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">Columna de Valor {operacion !== "count" ? "*" : ""}</Label>
                          <select value={columnaY} onChange={e => setColumnaY(e.target.value)}
                            className="w-full h-8 px-3 rounded-md border text-sm bg-white" data-testid="chart-col-y">
                            <option value="">{operacion === "count" ? "(Contar registros)" : "Seleccione columna..."}</option>
                            {columns.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                          {operacion === "count" && <p className="text-[9px] text-slate-400 mt-0.5">Opcional al contar</p>}
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs">Segmentar por (opcional)</Label>
                        <select value={segmentarPor} onChange={e => setSegmentarPor(e.target.value)}
                          className="w-full h-8 px-3 rounded-md border text-sm bg-white" data-testid="chart-segment-select">
                          <option value="">(Sin segmentacion - valor unico)</option>
                          {columns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <p className="text-[9px] text-slate-400 mt-0.5">Divide el valor por categoria. Ej: MON para separar RD$ y US$</p>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Formato de Valor</Label>
                          <select value={formato} onChange={e => setFormato(e.target.value)}
                            className="w-full h-8 px-3 rounded-md border text-sm bg-white" data-testid="chart-format-select">
                            {Object.entries(FORMAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">Tamanio en dashboard</Label>
                          <select value={size} onChange={e => setSize(e.target.value)}
                            className="w-full h-8 px-3 rounded-md border text-sm bg-white" data-testid="chart-size-select">
                            {Object.entries(SIZE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <Label className="text-xs">Color</Label>
                          <div className="flex gap-1 mt-1">
                            {CHART_COLORS.map(c => (
                              <button key={c} type="button" onClick={() => setColor(c)}
                                className={`w-5 h-5 rounded-full border-2 transition-all ${color === c ? "border-slate-800 scale-110" : "border-transparent"}`}
                                style={{ backgroundColor: c }} />
                            ))}
                          </div>
                        </div>
                        <label className="flex items-center gap-1.5 cursor-pointer mt-4">
                          <input type="checkbox" checked={mostrarConteo} onChange={e => setMostrarConteo(e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-slate-300" />
                          <span className="text-[10px] text-slate-500">Mostrar conteo de registros</span>
                        </label>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {/* ===== INFO FORM ===== */}
            {isInfo && (
              <>
                <div><Label className="text-xs">Titulo *</Label><Input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ej: Nota importante" className="h-8 text-sm" data-testid="chart-title-input" /></div>
                <div>
                  <Label className="text-xs">Contenido de la tarjeta *</Label>
                  <textarea value={contenidoTexto} onChange={e => setContenidoTexto(e.target.value)}
                    className="w-full h-28 px-3 py-2 rounded-md border text-sm bg-white resize-none"
                    placeholder="Escriba el contenido informativo de la tarjeta..."
                    data-testid="chart-info-textarea" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Tamanio</Label>
                    <select value={size} onChange={e => setSize(e.target.value)} className="w-full h-8 px-3 rounded-md border text-sm bg-white">
                      {Object.entries(SIZE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Color</Label>
                    <div className="flex gap-1 mt-1">
                      {CHART_COLORS.map(c => (
                        <button key={c} type="button" onClick={() => setColor(c)}
                          className={`w-5 h-5 rounded-full border-2 transition-all ${color === c ? "border-slate-800 scale-110" : "border-transparent"}`}
                          style={{ backgroundColor: c }} />
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ===== CHART FORM (bar, line, pie, donut) ===== */}
            {isChart && (
              <>
                <div><Label className="text-xs">Titulo *</Label><Input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ej: Prima por Aseguradora" className="h-8 text-sm" data-testid="chart-title-input" /></div>

                <div>
                  <Label className="text-xs">Coleccion de Datos *</Label>
                  <select value={coleccionId} onChange={e => { setColeccionId(e.target.value); setColumnaX(""); setColumnaY(""); setSegmentarPor(""); }}
                    className="w-full h-8 px-3 rounded-md border text-sm bg-white" data-testid="chart-collection-select">
                    <option value="">Seleccione...</option>
                    {collections.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.total_registros} reg.)</option>)}
                  </select>
                </div>

                <div>
                  <Label className="text-xs">Agrupar por (Eje X) *</Label>
                  <select value={columnaX} onChange={e => setColumnaX(e.target.value)}
                    className="w-full h-8 px-3 rounded-md border text-sm bg-white" data-testid="chart-col-x">
                    <option value="">Seleccione columna...</option>
                    {columns.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div>
                  <Label className="text-xs">Columna de Valor (Eje Y)</Label>
                  <select value={columnaY} onChange={e => setColumnaY(e.target.value)}
                    className="w-full h-8 px-3 rounded-md border text-sm bg-white" data-testid="chart-col-y">
                    <option value="">(Ninguna - solo contar)</option>
                    {columns.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Operacion</Label>
                    <select value={operacion} onChange={e => setOperacion(e.target.value)} className="w-full h-8 px-3 rounded-md border text-sm bg-white">
                      {Object.entries(OP_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Tamanio</Label>
                    <select value={size} onChange={e => setSize(e.target.value)} className="w-full h-8 px-3 rounded-md border text-sm bg-white">
                      {Object.entries(SIZE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Limite</Label>
                    <select value={limit} onChange={e => setLimit(Number(e.target.value))} className="w-full h-8 px-3 rounded-md border text-sm bg-white" data-testid="chart-limit-select">
                      {LIMIT_OPTIONS.map(n => <option key={n} value={n}>Top {n}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Color</Label>
                  <div className="flex gap-1.5 mt-1">
                    {CHART_COLORS.map(c => (
                      <button key={c} type="button" onClick={() => setColor(c)}
                        className={`w-5 h-5 rounded-full border-2 transition-all ${color === c ? "border-slate-800 scale-110" : "border-transparent"}`}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right: Preview */}
          <div className="bg-slate-50 rounded-lg p-3 border flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-slate-500">Vista Previa</p>
              {!isInfo && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handlePreview}
                  disabled={!coleccionId || loadingPreview} data-testid="chart-preview-btn">
                  {loadingPreview ? <Loader2 className="w-3 h-3 animate-spin" /> : "Actualizar"}
                </Button>
              )}
            </div>

            <div className="flex-1 flex items-center justify-center">
              {isInfo ? (
                <div className="bg-white rounded-lg border p-4 w-full">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{titulo || "Titulo"}</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{contenidoTexto || "Contenido..."}</p>
                </div>
              ) : isKpi && previewData ? (
                <div className="bg-white rounded-lg border p-4 w-full">
                  <div className="absolute top-0 left-0 w-full h-1 rounded-t-lg" style={{ backgroundColor: color }} />
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{titulo || "KPI"}</p>
                      {subtitulo && <p className="text-[10px] text-slate-400">{subtitulo}</p>}
                    </div>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "15" }}>
                      {operacion === "count" ? <ListOrdered className="w-3.5 h-3.5" style={{ color }} /> :
                       <DollarSign className="w-3.5 h-3.5" style={{ color }} />}
                    </div>
                  </div>
                  {previewData.segments?.length > 0 ? (
                    <div className="space-y-1.5">
                      {previewData.segments.map((s, i) => (
                        <div key={i} className="flex items-baseline justify-between border-b border-slate-100 pb-1 last:border-0">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color, opacity: 1 - (i * 0.3) }} />
                            <span className="text-[10px] font-medium text-slate-500">{s.label}</span>
                            {mostrarConteo && <span className="text-[8px] text-slate-300">({formatNumber(s.count)} reg.)</span>}
                          </div>
                          <span className="text-base font-bold tabular-nums" style={{ color }}>
                            {formatKpiValue(s.value, formato, s.label)}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-baseline justify-between pt-1 border-t border-slate-200">
                        <span className="text-[9px] font-semibold text-slate-400 uppercase">Total</span>
                        <span className="text-sm font-bold text-slate-700 tabular-nums">
                          {formatKpiValue(previewData.value, formato, "")}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-2xl font-bold tabular-nums" style={{ color }}>
                        {formatKpiValue(previewData.value || 0, formato, "")}
                      </p>
                      {columnaY && <p className="text-[9px] text-slate-400 mt-0.5">{OP_LABELS[operacion]} de {columnaY}</p>}
                    </div>
                  )}
                </div>
              ) : previewData && !isKpi ? (
                <div className="bg-white rounded border p-2 w-full">
                  <ResponsiveContainer width="100%" height={200}>
                    {tipo === "bar" ? (
                      <BarChart data={(previewData.labels || []).map((l, i) => ({ name: l.slice(0, 15), value: previewData.values[i] }))}>
                        <XAxis dataKey="name" tick={{ fontSize: 8 }} />
                        <YAxis tick={{ fontSize: 8 }} />
                        <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} />
                      </BarChart>
                    ) : tipo === "line" ? (
                      <LineChart data={(previewData.labels || []).map((l, i) => ({ name: l.slice(0, 15), value: previewData.values[i] }))}>
                        <XAxis dataKey="name" tick={{ fontSize: 8 }} />
                        <YAxis tick={{ fontSize: 8 }} />
                        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} />
                      </LineChart>
                    ) : (
                      <PieChart>
                        <Pie data={(previewData.labels || []).map((l, i) => ({ name: l, value: previewData.values[i] }))} cx="50%" cy="50%" innerRadius={tipo === "donut" ? 30 : 0} outerRadius={70} dataKey="value">
                          {(previewData.labels || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    )}
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center text-xs text-slate-400 py-8">
                  {isKpi && coleccionId ? (
                    <div><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-slate-300" /><p>Cargando vista previa...</p></div>
                  ) : (
                    <div>
                      <Hash className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p>{isKpi ? "Seleccione una coleccion para ver la vista previa" : "Configure y presione \"Actualizar\""}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Quick KPI templates */}
            {isKpi && coleccionId && columns.length > 0 && !chart && (
              <div className="mt-3 border-t pt-2">
                <p className="text-[10px] font-medium text-slate-400 mb-1.5">Plantillas rapidas:</p>
                <div className="flex flex-wrap gap-1">
                  {columns.filter(c => /prima|valor|balance|monto|total|importe/i.test(c)).map(col => (
                    <button key={col} type="button"
                      onClick={() => {
                        setTitulo(col.charAt(0) + col.slice(1).toLowerCase() + " Total");
                        setColumnaY(col);
                        setOperacion("sum");
                        setFormato("moneda");
                        const monCol = columns.find(c => /^mon$/i.test(c));
                        if (monCol) setSegmentarPor(monCol);
                      }}
                      className="px-2 py-1 rounded border border-indigo-200 bg-indigo-50 text-[10px] text-indigo-600 hover:bg-indigo-100 transition-colors"
                      data-testid={`template-${col}`}>
                      Suma de {col}
                    </button>
                  ))}
                  <button type="button"
                    onClick={() => { setTitulo("Total Registros"); setColumnaY(""); setOperacion("count"); setFormato("numero"); setSegmentarPor(""); }}
                    className="px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-[10px] text-emerald-600 hover:bg-emerald-100 transition-colors"
                    data-testid="template-count">
                    Contar Registros
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} data-testid="save-chart-btn">
            {chart ? "Guardar Cambios" : "Agregar Grafica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============== SHARE MODAL ==============
function ShareDashboardModal({ open, onOpenChange, dashboard, getAuthHeaders }) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateLink = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/dashboards-bi/dashboards/${dashboard.id}/share`, {}, getAuthHeaders());
      setToken(res.data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error generando enlace");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setToken(""); setCopied(false); }
  }, [open]);

  const publicUrl = token ? `${window.location.origin}/public/dashboard/${token}` : "";

  const copyToClipboard = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success("Enlace copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle>Compartir Dashboard</DialogTitle>
          <DialogDescription>Genere un enlace publico que cualquier persona podra ver sin necesidad de iniciar sesion.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Dashboard: <strong>{dashboard.nombre}</strong></p>
          {!token ? (
            <Button onClick={generateLink} disabled={loading} className="w-full" data-testid="generate-link-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
              Generar Enlace Publico
            </Button>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input value={publicUrl} readOnly className="text-xs" data-testid="public-url-input" />
                <Button variant="outline" size="sm" onClick={copyToClipboard}>
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-400">Cualquier persona con este enlace podra ver el dashboard y sus graficas actualizadas en tiempo real.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
